// KLAReportPage4.js
import React, { forwardRef } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

const PAGE1_MAX_WEIGHT = 9;
const SUBSEQUENT_MAX_WEIGHT = 18;

const flattenUtteranceData = (lineByTag, extras) => {
  const result = [];
  Object.entries(lineByTag).forEach(([tag, lines]) => {
    lines.forEach((line) => {
      const lineNumber = parseInt(line.split(".")[0], 10);
      result.push({ tag, line, extras: extras[lineNumber] || [] });
    });
  });
  return result;
};

const renderUtteranceTable = (utterances) => {
  // ✅ 태그별 rowSpan 계산을 위해 그룹 단위로 처리
  const tagCounts = {};
  utterances.forEach((entry) => {
    tagCounts[entry.tag] = (tagCounts[entry.tag] || 0) + 1;
  });

  //let previousTag = null;
  let tagRenderedCount = {};

  return (
    <table
      className="utterance-list"
      style={{
        width: "100%",
        tableLayout: "auto",
        borderCollapse: "collapse",
        fontFamily: "Noto Sans KR",
        marginTop: "20px",
      }}
    >
      <tbody>
        <tr>
          <td
            colSpan="2"
            style={{
              fontSize: "20px",
              backgroundColor: "#f0f0f0",
              border: "none",
            }}
          >
            [] 있는 발화 목록
          </td>
        </tr>
        <tr>
          <td
            style={{
              width: "20%",
              fontSize: "20px",
              backgroundColor: "#ffffff",
              border: "1px solid black",
              textAlign: "center",
            }}
          >
            태그 종류
          </td>
          <td
            style={{
              width: "80%",
              fontSize: "20px",
              backgroundColor: "#ffffff",
              border: "1px solid black",
              textAlign: "center",
            }}
          >
            발화 예시
          </td>
        </tr>

        {utterances.map((entry, idx) => {
          //console.log(
          //  `렌더링 entry: ${entry.line}, tag: ${entry.tag}, index: ${idx}, extras: ${entry.extras}`
          //);
          const tag = entry.tag;
          const isFirstOfGroup = !tagRenderedCount[tag];

          // ✅ 현재 tag의 출력 여부를 체크
          if (!tagRenderedCount[tag]) tagRenderedCount[tag] = 1;
          else tagRenderedCount[tag] += 1;

          return (
            <tr key={idx}>
              {/* ✅ 태그 종류 셀 병합 */}
              {isFirstOfGroup && (
                <td
                  rowSpan={tagCounts[tag]}
                  style={{
                    fontSize: "16px",
                    backgroundColor: "#ffffff",
                    border: "1px solid black",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  {tag}
                </td>
              )}
              {/* 발화 예시 */}
              <td
                style={{
                  fontSize: "16px",
                  backgroundColor: "#ffffff",
                  border: "1px solid black",
                  textAlign: "left",
                  verticalAlign: "middle",
                  padding: "8px",
                  wordBreak: "break-word", // ✅ 줄바꿈 강제
                  whiteSpace: "normal", // ✅ 공백 기준 줄바꿈 허용
                  lineHeight: "1.5", // ✅ 줄 간격 확보
                }}
              >
                <div
                  style={{
                    display: "block",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {entry.line}
                </div>
                {/* {entry.extras.length > 0 &&
                  entry.extras.map((extra, i) => (
                    <div
                      key={i}
                      style={{
                        display: "block", // ✅ block 요소로 강제 줄바꿈
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        marginTop: "4px",
                      }}
                    >
                      {extra}
                    </div>
                  ))} */}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const KLAReportPage4 = forwardRef(
  ({ customData, setCurrentPage, subPage, isThumbnailView }, ref) => {
    const utteranceList = flattenUtteranceData(
      customData.lineByTag,
      customData.extras
    );

    const getVisualWeight = (str) => {
      return [...str].reduce((sum, char) => {
        const code = char.charCodeAt(0);
        // 한글 및 자모는 시각적으로 넓다고 가정
        if (
          (code >= 0xac00 && code <= 0xd7af) || // 한글
          (code >= 0x3130 && code <= 0x318f) || // 자모
          (code >= 0x1100 && code <= 0x11ff)
        ) {
          // 옛한글 자모
          return sum + 2;
        }
        return sum + 1; // 영어/숫자/기호 등
      }, 0);
    };

    const splitByWeight = (entries, maxWeight) => {
      const pages = [];
      let currentPage = [];
      let currentWeight = 0;

      entries.forEach((entry) => {
        const visualLength = getVisualWeight(entry.line);
        const weight = visualLength > 82 ? 1.5 : 1;
        //const weight = entry.line.length > 80 ? 1.5 : 1; // ✨ 긴 줄은 1.5로 처리
        //console.log(
        //  `Processing entry: ${entry.line}, length: ${visualLength}, weight: ${weight}, currentWeight: ${currentWeight}`
        //);
        if (currentWeight + weight > maxWeight) {
          pages.push(currentPage);
          currentPage = [];
          currentWeight = 0;
        }
        currentPage.push(entry);
        currentWeight += weight;
      });

      if (currentPage.length > 0) {
        pages.push(currentPage);
      }

      return pages;
    };

    // 페이지 분할
    // const firstPageUtterances = utteranceList.slice(0, PAGE1_MAX);
    // const remainingUtterances = utteranceList.slice(PAGE1_MAX);

    // const subsequentPages = [];
    // for (let i = 0; i < remainingUtterances.length; i += SUBSEQUENT_MAX) {
    //   subsequentPages.push(remainingUtterances.slice(i, i + SUBSEQUENT_MAX));
    // }

    const firstSplit = splitByWeight(utteranceList, PAGE1_MAX_WEIGHT);
    const firstPageUtterances = firstSplit[0] || [];
    const remainingUtterances = utteranceList.slice(firstPageUtterances.length);
    const subsequentPages = splitByWeight(
      remainingUtterances,
      SUBSEQUENT_MAX_WEIGHT
    );

    const renderHeader = () => (
      <Box className="header-section" sx={{ marginBottom: 2 }}>
        {/* Title Section */}
        <Box className="kla-title" sx={{ marginBottom: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontFamily: "Noto Sans KR" }}
          >
            <span style={{ color: "#4b2354", fontWeight: "bold" }}>KLA</span>
            <span style={{ color: "#999" }}> Korean Language Analysis</span>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontFamily: "Noto Sans KR",
              fontSize: "20px",
            }}
          >
            <Typography
              sx={{ fontSize: "20px !important", fontWeight: "normal" }}
            >
              검사일 | {customData.info.examdate}
            </Typography>
            <Typography
              sx={{ fontSize: "20px !important", fontWeight: "normal" }}
            >
              이름 | {customData.info.name}
            </Typography>
            <Typography
              sx={{ fontSize: "20px !important", fontWeight: "normal" }}
            >
              생활연령 | {customData.info.age_years}세{" "}
              {customData.info.age_months}개월
            </Typography>
            <Typography
              sx={{ fontSize: "20px !important", fontWeight: "normal" }}
            >
              학년 | {customData.info.grade}
            </Typography>
          </Box>
        </Box>
      </Box>
    );

    const renderDataType = () => (
      <Box className="kla-report-page3-data-type" sx={{ marginBottom: 4 }}>
        <Typography
          variant="h5"
          sx={{ fontFamily: "Noto Sans KR", marginBottom: 2 }}
        >
          자료 유형
        </Typography>
        <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
          {/* 자발화 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#E6E0F8",
              padding: "6px 12px",
              borderRadius: "4px",
              flexGrow: 1,
              justifyContent: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={customData.info.utteranceType === "자발화"}
                  sx={{ transform: "scale(1.0)" }}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: "20px", fontFamily: "Noto Sans KR" }}
                >
                  자발화
                </Typography>
              }
              sx={{ fontFamily: "Noto Sans KR" }}
            />
          </Box>

          {/* 이야기 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#E6E0F8",
              padding: "6px 12px",
              borderRadius: "4px",
              flexGrow: 1,
              justifyContent: "center",
            }}
          >
            <Checkbox
              checked={["이야기(회상산출)", "이야기(자발산출)"].includes(
                customData.info.utteranceType
              )}
              sx={{ transform: "scale(1.0)", marginRight: "0px" }}
            />
            <Typography
              sx={{
                fontSize: "20px",
                fontFamily: "Noto Sans KR",
                marginRight: "12px",
              }}
            >
              이야기
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontFamily: "Noto Sans KR",
                marginRight: "4px",
              }}
            >
              (
            </Typography>
            <Checkbox
              checked={customData.info.utteranceType === "이야기(회상산출)"}
              sx={{ transform: "scale(1.0)", padding: 0, marginRight: "4px" }}
            />
            <Typography sx={{ fontSize: "20px", fontFamily: "Noto Sans KR" }}>
              회상산출
            </Typography>
            <Checkbox
              checked={customData.info.utteranceType === "이야기(자발산출)"}
              sx={{
                transform: "scale(1.0)",
                padding: 0,
                marginRight: "4px",
                marginLeft: "12px",
              }}
            />
            <Typography sx={{ fontSize: "20px", fontFamily: "Noto Sans KR" }}>
              자발산출
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontFamily: "Noto Sans KR",
                marginLeft: "4px",
              }}
            >
              )
            </Typography>
          </Box>

          {/* 작문 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#E6E0F8",
              padding: "6px 12px",
              borderRadius: "4px",
              flexGrow: 1,
              justifyContent: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox checked={customData.info.utteranceType === "작문"} />
              }
              label={
                <Typography
                  sx={{ fontSize: "20px", fontFamily: "Noto Sans KR" }}
                >
                  작문
                </Typography>
              }
              sx={{ fontFamily: "Noto Sans KR" }}
            />
          </Box>
        </Box>
      </Box>
    );

    const renderPage = (utterances, pageIndex, showHeaderAndInfo) => (
      <Box
        key={pageIndex}
        onClick={() => setCurrentPage?.(pageIndex + 1)}
        ref={(el) => {
          if (!ref.current) ref.current = [];
          ref.current[pageIndex] = el;
        }}
        sx={pageStyle}
      >
        <Paper sx={paperStyle}>
          {renderHeader()}
          {renderDataType()}
          {showHeaderAndInfo && (
            <Box sx={{ marginTop: 2 }}>
              <Typography
                variant="h5"
                sx={{ fontFamily: "Noto Sans KR", marginBottom: 2 }}
              >
                분석 결과
              </Typography>
              <table
                className="analysis-table"
                style={{
                  width: "100%",
                  tableLayout: "auto",
                  borderCollapse: "collapse",
                  fontFamily: "Noto Sans KR",
                }}
              >
                <tbody>
                  <tr>
                    <td
                      colSpan="3"
                      style={{
                        fontSize: "20px",
                        backgroundColor: "#f0f0f0",
                        border: "none",
                      }}
                    >
                      발화 정보
                    </td>
                  </tr>
                  <tr>
                    <td rowSpan="4" style={cellLeft}>
                      이해가능도별
                      <br />
                      발화수
                    </td>
                    <td style={cellMid}>전체발화수</td>
                    <td style={cellRight}>{customData.num_line_total[0]}개</td>
                  </tr>
                  <tr>
                    <td style={cellMid}>이해 불가</td>
                    <td style={cellRight}>
                      {customData.num_line_unknown[0]}개
                    </td>
                  </tr>
                  <tr>
                    <td style={cellMid}>부분 이해 가능</td>
                    <td style={cellRight}>
                      {customData.num_line_partial[0]}개
                    </td>
                  </tr>
                  <tr>
                    <td style={cellMid}>완전 이해 가능</td>
                    <td style={cellRight}>{customData.num_line_known[0]}개</td>
                  </tr>
                  <tr>
                    <td rowSpan="5" style={cellLeft}>
                      이해가능발화
                      <br />
                      유형분석
                    </td>
                    <td style={cellMid}>평서문 발화(.)</td>
                    <td style={cellRight}>
                      {customData.num_line_end_period[0]}개
                    </td>
                  </tr>
                  <tr>
                    <td style={cellMid}>의문문 발화(?)</td>
                    <td style={cellRight}>
                      {customData.num_line_end_question[0]}개
                    </td>
                  </tr>
                  <tr>
                    <td style={cellMid}>감탄문 발화(!)</td>
                    <td style={cellRight}>
                      {customData.num_line_end_exclamation[0]}개
                    </td>
                  </tr>
                  <tr>
                    <td style={cellMid}>중첩 발화(&gt;)</td>
                    <td style={cellRight}>
                      {customData.num_line_end_bracket[0]}개
                    </td>
                  </tr>
                  <tr>
                    <td style={cellMid}>미완성 발화(^)</td>
                    <td style={cellRight}>
                      {customData.num_line_end_exponential[0]}개
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
          )}
          {renderUtteranceTable(utterances)}
        </Paper>
      </Box>
    );

    const allPages = [
      renderPage(firstPageUtterances, 0, true),
      ...subsequentPages.map((u, i) => renderPage(u, i + 1, false)),
    ];

    return (
      <Box>
        {isThumbnailView
          ? allPages
          : allPages.filter((_, i) => i === subPage - 1)}
      </Box>
    );
  }
);

const pageStyle = {
  width: "794px",
  height: "1123px",
  padding: 3,
  backgroundColor: "white",
  boxShadow: 3,
  margin: "20px",
  pageBreakAfter: "always",
  aspectRatio: "794 / 1123",
};

const paperStyle = {
  padding: 4,
  height: "93%",
  border: "3px solid #4b2354",
};

const cellLeft = {
  position: "relative",
  zIndex: 10,
  width: "25%",
  fontSize: "20px",
  backgroundColor: "#ffffff",
  border: "1px solid black",
  textAlign: "center",
  verticalAlign: "middle",
};

const cellMid = {
  width: "40%",
  fontSize: "20px",
  backgroundColor: "#ffffff",
  border: "1px solid black",
  textAlign: "center",
  verticalAlign: "middle",
};

const cellRight = {
  width: "35%",
  fontSize: "20px",
  backgroundColor: "#ffffff",
  border: "1px solid black",
  textAlign: "center",
  verticalAlign: "middle",
};

export default KLAReportPage4;
